import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { auto } from "@popperjs/core";
import { LoadProjectData } from "../../helpers/actions/RequestData";
import Modal from "react-bootstrap/Modal";
import { createProjectMaintenance } from "../../helpers/axios/maintenanceAxios";
import { set } from "lodash";
import {
  MAINTENANCE_DATA,
  updateGlobalState,
} from "../../helpers/reducers/GlobalReducer";
import {
  agGridOptionsNone,
  agGridTheme,
  calculateNextDueDate,
} from "../../helpers/constants/Constants";
import { use } from "react";
import LoadingScreen from "../loadingScreen/LoadingScreen";

const Maintenance = () => {
  // Assuming state.maintenance.projects is an array of maintenance items
  const {
    maintenanceData,
    selectedProject,
    allDocs,
    cloudFiles,
    superUserDetails,
    numberOfLoadedGlobalModels,
    totalGlobalModels,
  } = useSelector((state) => state.globalReducer);
  const { modelDataProjectId, modelData, modelDataLoaded } = useSelector(
    (state) => state.modelReducer
  );
  const [selectedRow, setSelectedRow] = useState([]);
  const [gridData, setGridData] = useState(null);

  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordInput, setRecordInput] = useState({
    createdByEmail: superUserDetails?.email,
    projectId: selectedProject[0]?.mviewProjectInfo?.id,
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (maintenanceData?.length > 0) {
      setGridData(maintenanceData);
    }
  }, [maintenanceData]);

  useEffect(() => {
    if (allDocs?.length > 0 && cloudFiles?.length > 0) {
      LoadProjectData(
        dispatch,
        selectedProject,
        allDocs,
        cloudFiles,
        modelDataProjectId,
        modelData
      );
    }
  }, [allDocs, cloudFiles]);

  const columnDefs = [
    {
      headerName: "Tag",
      field: "tag",
      sortable: true,
      filter: true,
      valueGetter: (params) => {
        return params.data.projectObject?.tag || "N/A";
      },
    },

    { headerName: "MUID", field: "muid", sortable: true, filter: true },
    {
      headerName: "Interval",
      field: "interval",
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        if (!params.data.dimension) return params.data.interval;
        const units = { D: "Day(s)", M: "Month(s)", Y: "Year(s)" };
        const unit = units[params.data.dimension] || "";
        const interval = `${params.data.interval} ${unit}`;
        return (
          interval +
          (params.data.intervalHours
            ? ` or ${Number(
                params.data.intervalHours
              ).toLocaleString()} Hour(s)`
            : "")
        );
      },
    },
    {
      headerName: "Description",
      field: "description",
      sortable: true,
      filter: true,
      autoHeight: true,
      cellStyle: { whiteSpace: "normal", lineHeight: "1.5" },
      minWidth: 600,
    },
    {
      headerName: "Last Performed",
      field: "lastPerformed",
      sortable: true,
      filter: true,
      //dynamically change the colour of the cell based on the date
      valueFormatter: (params) => {
        if (!params.value) return "N/A"; // Handle cases where nextDue is null or undefined
        const date = new Date(params.value);
        return date.toLocaleDateString("en-GB", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      },
    },
    {
      headerName: "Next Due",
      field: "nextDue",
      sortable: true,
      filter: true,
      //if the nextDue date is in the past, change the colour of the cell to red using bg-danger
      cellClass: (params) => {
        return params.value && new Date(params.value) > new Date()
          ? ""
          : "bg-danger";
      },
      valueFormatter: (params) => {
        if (!params.value) return "N/A"; // Handle cases where nextDue is null or undefined
        const date = new Date(params.value);
        return date.toLocaleDateString("en-GB", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      },
    },
  ];

  const gridRef = useRef(null);

  return (
    <>
      {!modelDataLoaded && (
        <LoadingScreen
          input="Fetching Project Data"
          position="fixed"
          width="20"
          marginTop="3%"
          showModelLoadProgress={true}
          numberOfLoadedGlobalModels={numberOfLoadedGlobalModels}
          totalGlobalModels={totalGlobalModels.length}
        />
      )}
      <Modal
        show={showRecordModal}
        onHide={(e) => {
          setShowRecordModal(false);
        }}
      >
        <Modal.Header
          closeButton={(e) => {
            setShowRecordModal(false);
          }}
        >
          <Modal.Title>Confirm Add</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close"
              onClick={(e) => {
                setShowRecordModal(false);
              }}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          {/* Recieve the input from the user for the modal name and keys */}
          {selectedRow && selectedRow.length > 0 && (
            <div>
              <h5>Maintenance Details</h5>
              <div className="mb-3">
                <strong>MUID:</strong> {selectedRow[0].muid}
                <br />
                <strong>Interval:</strong> {selectedRow[0].interval}{" "}
                {selectedRow[0].dimension
                  ? { D: "Day(s)", M: "Month(s)", Y: "Year(s)" }[
                      selectedRow[0].dimension
                    ] || ""
                  : ""}
                {selectedRow[0].intervalHours
                  ? ` or ${Number(
                      selectedRow[0].intervalHours
                    ).toLocaleString()} Hour(s)`
                  : ""}
                <br />
                <strong>Description:</strong> {selectedRow[0].description}
                <br />
                <strong>Last Performed:</strong>{" "}
                {selectedRow[0].lastPerformed
                  ? new Date(selectedRow[0].lastPerformed).toLocaleDateString(
                      "en-GB"
                    )
                  : "N/A"}
                <br />
                <strong>Next Due:</strong>{" "}
                {selectedRow[0].nextDue
                  ? new Date(selectedRow[0].nextDue).toLocaleDateString("en-GB")
                  : "N/A"}
                <br />
              </div>
              <h6>History</h6>
              <div
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  border: "1px solid #eee",
                  borderRadius: 4,
                  padding: 8,
                  marginBottom: 16,
                }}
              >
                {Array.isArray(selectedRow[0].history) &&
                selectedRow[0].history.length > 0 ? (
                  selectedRow[0].history.map((rec, idx) => (
                    <div
                      key={idx}
                      style={{
                        borderBottom: "1px solid #ddd",
                        marginBottom: 8,
                        paddingBottom: 8,
                      }}
                    >
                      <div>
                        <strong>Date:</strong>{" "}
                        {rec.createdDate
                          ? new Date(rec.createdDate).toLocaleDateString(
                              "en-GB"
                            )
                          : "N/A"}
                      </div>
                      <div>
                        <strong>Performed By:</strong>{" "}
                        {rec.createdByEmail || "N/A"}
                      </div>
                      <div>
                        <strong>Action:</strong> {rec.action || "N/A"}
                      </div>
                      {rec.hours && (
                        <div>
                          <strong>Hours:</strong> {rec.hours}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div>No history records found.</div>
                )}
              </div>
              <h6>Record New Maintenance</h6>
              <div className="mb-2">
                <label className="form-label">
                  Description of Actions Carried Out
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={recordInput.action || ""}
                  onChange={(e) =>
                    setRecordInput({ ...recordInput, action: e.target.value })
                  }
                  placeholder="Describe the actions carried out"
                />
              </div>
              {selectedRow[0].intervalHours && (
                <div className="mb-2">
                  <label className="form-label">Number of Hours</label>
                  <input
                    type="number"
                    className="form-control"
                    value={recordInput.hours || ""}
                    min={0}
                    onChange={(e) =>
                      setRecordInput({ ...recordInput, hours: e.target.value })
                    }
                    placeholder="Enter number of hours"
                  />
                </div>
              )}
            </div>
          )}
          <div className="d-flex justify-content-end p-1">
            <button
              className="btn btn-secondary btn-sm"
              onClick={(e) => {
                setShowRecordModal(false);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-success btn-sm"
              onClick={(e) => {
                createProjectMaintenance(recordInput)
                  .then((maintenance) => {
                    //Update the maintenance data in the global state
                    const updatedMaintenanceData = maintenanceData.map(
                      (item) => {
                        if (
                          item.staticObjectMaintenanceId ===
                          selectedRow[0].staticObjectMaintenanceId
                        ) {
                          return {
                            ...item,
                            lastPerformed: maintenance.createdDate,
                            nextDue: calculateNextDueDate(
                              new Date(maintenance.createdDate),
                              item.dimension,
                              item.interval
                            ),
                            history: [
                              ...item.history,
                              {
                                ...maintenance,
                              },
                            ],
                          };
                        }
                        return item;
                      }
                    );

                    dispatch(
                      updateGlobalState({
                        value: [...updatedMaintenanceData],
                        stateName: MAINTENANCE_DATA,
                      })
                    );
                    setShowRecordModal(false);
                  })
                  .catch(console.log);
              }}
            >
              Record
            </button>
          </div>
        </Modal.Body>
      </Modal>
      <div
        className="pt-1"
        style={{
          paddingLeft: "65px",
          paddingRight: "0px",
          height: "90vh",
          overflowY: auto,
        }}
      >
        <h1>Maintenance</h1>
        <div className="d-flex justify-content-between align-items-start w-100">
          <div className="d-flex">
            <button
              disabled={!selectedRow || selectedRow.length == 0}
              className="btn btn-primary btn-sm"
              onClick={(e) => {
                setShowRecordModal(true);
                setRecordInput({
                  ...recordInput,
                  projectObjectId: selectedRow[0].projectObjectId,
                  staticObjectMaintenanceId:
                    selectedRow[0].staticObjectMaintenanceId,
                  dimension: selectedRow[0].dimension,
                  interval: selectedRow[0].interval,
                  intervalHours: selectedRow[0].intervalHours,
                  description: selectedRow[0].description,
                  lastPerformed: selectedRow[0].lastPerformed,
                  nextDue: selectedRow[0].nextDue,
                });
              }}
            >
              View/Record
            </button>
          </div>
        </div>
        <div id="gridStyle" className=" mt-2" style={{ height: "70VH" }}>
          <AgGridReact
            theme={agGridTheme}
            ref={gridRef}
            cellSelection={{
              mode: "single",
              handle: {
                mode: "fill",
                direction: "y", // Fill Handle can only be dragged horizontally
              },
            }}
            gridOptions={{
              ...agGridOptionsNone,
              getRowClass: (params) => {
                return params.data.nextDue &&
                  new Date(params.data.nextDue) > new Date()
                  ? ""
                  : "bg-danger";
              },
            }}
            onSelectionChanged={(e) => {
              setSelectedRow(e.api.getSelectedRows());
            }}
            rowData={gridData}
            columnDefs={columnDefs}
          />
        </div>
      </div>
    </>
  );
};

export default Maintenance;
